import { LitElement, css, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';

import '@awesome.me/webawesome/dist/components/button/button.js';
import '@awesome.me/webawesome/dist/components/callout/callout.js';
import '@awesome.me/webawesome/dist/components/dialog/dialog.js';
import '@awesome.me/webawesome/dist/components/input/input.js';
import '@awesome.me/webawesome/dist/components/option/option.js';
import '@awesome.me/webawesome/dist/components/select/select.js';
import '@awesome.me/webawesome/dist/components/spinner/spinner.js';
import '@awesome.me/webawesome/dist/components/tag/tag.js';
import '@awesome.me/webawesome/dist/components/textarea/textarea.js';

import {
  ApiError,
  acceptedFileTypes,
  api,
  documentTypeLabels,
  formatBytes,
  formatIban,
  formValues,
  linkKindLabels,
  type BankAccount,
  type Client,
  type Contract,
  type Document,
  type DocumentLinkInput,
  type DocumentMetadata,
  type DocumentType,
  type Employee,
  type ExtractResult,
  type LinkKind,
} from '../api.js';
import { replaceUrl } from '../router.js';
import { pageStyles } from '../shared-styles.js';
import '../components/audit-dialog.js';
import type { AuditDialog } from '../components/audit-dialog.js';

interface Filters {
  type: string;
  q: string;
  employee_id: string;
  client_id: string;
  contract_id: string;
  bank_account_id: string;
  bank_transaction_id: string;
}

const typeOptions = Object.entries(documentTypeLabels) as [DocumentType, string][];

@customElement('documents-page')
export class DocumentsPage extends LitElement {
  static styles = [
    pageStyles,
    css`
      /* Fixed column widths: long file names and link chips wrap inside
         their columns instead of squeezing the title. */
      @media (min-width: 901px) {
        table {
          table-layout: fixed;
        }
        th.c-date {
          width: 9%;
        }
        th.c-type {
          width: 11%;
        }
        th.c-title {
          width: 20%;
        }
        th.c-counterparty {
          width: 11%;
        }
        th.c-amount {
          width: 8%;
        }
        th.c-linked {
          width: 15%;
        }
        th.c-file {
          width: 11%;
        }
        th.c-actions {
          width: 15%;
        }
        td.actions {
          white-space: normal;
        }
      }
      /* Whole-page drop target while a file is dragged over the page. */
      .drop-overlay {
        position: fixed;
        inset: 0.5rem;
        z-index: 50;
        display: grid;
        place-items: center;
        border: 3px dashed var(--wa-color-brand-border-loud, #36c);
        border-radius: var(--wa-border-radius-l, 10px);
        background: color-mix(in srgb, var(--wa-color-brand-fill-quiet, #eef3ff) 85%, transparent);
        color: var(--wa-color-brand-on-quiet, #24c);
        font-size: 1.25rem;
        font-weight: 600;
        pointer-events: none;
      }
      .picked {
        display: flex;
        gap: 1rem;
        align-items: center;
        flex-wrap: wrap;
      }
      .picked img {
        max-height: 9rem;
        max-width: 12rem;
        border-radius: var(--wa-border-radius-m, 6px);
        border: 1px solid var(--wa-color-neutral-border-quiet, #ddd);
      }
      .picked .meta {
        flex: 1;
        min-width: 12rem;
      }
      .reading {
        display: inline-flex;
        align-items: center;
        gap: 0.5rem;
        color: var(--wa-color-text-quiet, #666);
      }
      details.text {
        grid-column: 1 / -1;
      }
      details.text pre {
        max-height: 14rem;
        overflow: auto;
        font-size: 0.8rem;
        white-space: pre-wrap;
        background: var(--wa-color-neutral-fill-quiet, #f4f4f4);
        padding: 0.5rem;
        border-radius: var(--wa-border-radius-m, 6px);
      }
    `,
  ];

  @state() private items: Document[] = [];
  @state() private employees: Employee[] = [];
  @state() private clients: Client[] = [];
  @state() private contracts: Contract[] = [];
  @state() private bankAccounts: BankAccount[] = [];
  @state() private loading = true;
  @state() private error = '';
  @state() private filters: Filters = { type: '', q: '', employee_id: '', client_id: '', contract_id: '', bank_account_id: '', bank_transaction_id: '' };

  @state() private dialogOpen = false;
  @state() private editing: Document | null = null;
  @state() private saving = false;
  @state() private formError = '';
  @state() private fieldErrors: Record<string, string> = {};
  // Link pickers hold ids as strings because wa-select values are strings.
  @state() private pickedEmployees: string[] = [];
  @state() private pickedClients: string[] = [];
  @state() private pickedContracts: string[] = [];
  @state() private pickedBankAccounts: string[] = [];
  // New upload: the chosen file, its preview and what the server read from it.
  @state() private pendingFile: File | null = null;
  @state() private previewUrl = '';
  @state() private extracting = false;
  @state() private extractWarnings: string[] = [];
  @state() private recognisedText = '';
  @state() private draft: Partial<DocumentMetadata> = {};
  @state() private dragging = false;
  // Fields the user edited since the dialog opened; suggestions never overwrite them.
  private touched = new Set<string>();
  private dragDepth = 0;

  private searchTimer: number | undefined;

  connectedCallback() {
    super.connectedCallback();
    const p = new URLSearchParams(window.location.search);
    this.filters = {
      type: p.get('type') ?? '',
      q: p.get('q') ?? '',
      employee_id: p.get('employee_id') ?? '',
      client_id: p.get('client_id') ?? '',
      contract_id: p.get('contract_id') ?? '',
      bank_account_id: p.get('bank_account_id') ?? '',
      bank_transaction_id: p.get('bank_transaction_id') ?? '',
    };
    void this.loadReferenceData();
    void this.load();
    this.addEventListener('dragenter', this.onDragEnter);
    this.addEventListener('dragover', this.onDragOver);
    this.addEventListener('dragleave', this.onDragLeave);
    this.addEventListener('drop', this.onDrop);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('dragenter', this.onDragEnter);
    this.removeEventListener('dragover', this.onDragOver);
    this.removeEventListener('dragleave', this.onDragLeave);
    this.removeEventListener('drop', this.onDrop);
    this.revokePreview();
  }

  // ---- drag and drop onto the page ----

  private hasFiles(e: DragEvent): boolean {
    return Array.from(e.dataTransfer?.types ?? []).includes('Files');
  }

  private onDragEnter = (e: DragEvent) => {
    if (!this.hasFiles(e)) return;
    e.preventDefault();
    this.dragDepth++;
    this.dragging = true;
  };

  private onDragOver = (e: DragEvent) => {
    if (!this.hasFiles(e)) return;
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
  };

  // dragleave fires for every child the pointer leaves; the depth counter
  // tells a real exit from the page apart from moving between children.
  private onDragLeave = (e: DragEvent) => {
    if (!this.hasFiles(e)) return;
    this.dragDepth = Math.max(0, this.dragDepth - 1);
    if (this.dragDepth === 0) this.dragging = false;
  };

  private onDrop = (e: DragEvent) => {
    if (!this.hasFiles(e)) return;
    e.preventDefault();
    this.dragDepth = 0;
    this.dragging = false;
    const files = Array.from(e.dataTransfer?.files ?? []);
    if (files.length === 0) return;
    if (files.length > 1) this.error = 'One file at a time: the first one was taken.';
    this.openWithFile(files[0]);
  };

  // ---- file picker ----

  private openPicker() {
    const input = this.renderRoot.querySelector('input.picker') as HTMLInputElement | null;
    if (!input) return;
    input.value = '';
    input.click();
  }

  private onPicked(e: Event) {
    const file = (e.target as HTMLInputElement).files?.[0];
    if (file) this.openWithFile(file);
  }

  private async loadReferenceData() {
    try {
      [this.employees, this.clients, this.contracts, this.bankAccounts] = await Promise.all([
        api.get<Employee[]>('/api/employees'),
        api.get<Client[]>('/api/clients'),
        api.get<Contract[]>('/api/contracts'),
        api.get<BankAccount[]>('/api/bank-accounts'),
      ]);
    } catch (e) {
      this.error = (e as Error).message;
    }
  }

  private queryString(): string {
    const p = new URLSearchParams();
    for (const [k, v] of Object.entries(this.filters)) {
      if (v) p.set(k, v);
    }
    const s = p.toString();
    return s ? `?${s}` : '';
  }

  private async load() {
    this.loading = true;
    this.error = '';
    replaceUrl('/documents' + this.queryString());
    try {
      this.items = await api.get<Document[]>('/api/documents' + this.queryString());
    } catch (e) {
      this.error = (e as Error).message;
    } finally {
      this.loading = false;
    }
  }

  private setFilter(key: keyof Filters, value: string) {
    this.filters = { ...this.filters, [key]: value };
    void this.load();
  }

  private onSearchInput(e: Event) {
    const value = (e.target as HTMLInputElement).value;
    window.clearTimeout(this.searchTimer);
    this.searchTimer = window.setTimeout(() => this.setFilter('q', value.trim()), 300);
  }

  /** The object filter currently applied, for the removable chip. */
  private activeObjectFilter(): { kind: LinkKind; label: string } | null {
    const f = this.filters;
    if (f.employee_id) {
      return { kind: 'employee', label: this.employees.find((e) => String(e.id) === f.employee_id)?.name ?? `employee #${f.employee_id}` };
    }
    if (f.client_id) {
      return { kind: 'client', label: this.clients.find((c) => String(c.id) === f.client_id)?.name ?? `client #${f.client_id}` };
    }
    if (f.contract_id) {
      return { kind: 'contract', label: this.contractLabel(this.contracts.find((c) => String(c.id) === f.contract_id)) ?? `contract #${f.contract_id}` };
    }
    if (f.bank_account_id) {
      return { kind: 'bank_account', label: this.accountLabel(this.bankAccounts.find((a) => String(a.id) === f.bank_account_id)) ?? `account #${f.bank_account_id}` };
    }
    if (f.bank_transaction_id) {
      return { kind: 'bank_transaction', label: `#${f.bank_transaction_id}` };
    }
    return null;
  }

  private contractLabel(c: Contract | undefined): string | undefined {
    if (!c) return undefined;
    return `${c.employee_name} @ ${c.client_name} (${c.period.start} – ${c.period.end ?? 'open'})`;
  }

  private accountLabel(a: BankAccount | undefined): string | undefined {
    if (!a) return undefined;
    return `${a.label} · ${formatIban(a.iban)}`;
  }

  private clearObjectFilter() {
    this.filters = { ...this.filters, employee_id: '', client_id: '', contract_id: '', bank_account_id: '', bank_transaction_id: '' };
    void this.load();
  }

  // ---- dialog ----

  /** Opens the upload dialog for a chosen file and asks the server what it can read from it. */
  private openWithFile(file: File) {
    this.editing = null;
    // Preselect the object the page is filtered by.
    this.pickedEmployees = this.filters.employee_id ? [this.filters.employee_id] : [];
    this.pickedClients = this.filters.client_id ? [this.filters.client_id] : [];
    this.pickedContracts = this.filters.contract_id ? [this.filters.contract_id] : [];
    this.pickedBankAccounts = this.filters.bank_account_id ? [this.filters.bank_account_id] : [];
    this.resetFormState();
    this.revokePreview();
    this.pendingFile = file;
    this.previewUrl = file.type.startsWith('image/') ? URL.createObjectURL(file) : '';
    this.draft = { type: 'receipt', title: file.name.replace(/\.[^.]+$/, '') };
    this.touched = new Set();
    this.extractWarnings = [];
    this.recognisedText = '';
    this.dialogOpen = true;
    void this.runExtract(file);
  }

  private async runExtract(file: File) {
    this.extracting = true;
    try {
      const fd = new FormData();
      fd.append('file', file, file.name);
      const res = await api.upload<ExtractResult>('/api/documents/extract', fd);
      if (this.pendingFile !== file) return; // the user picked another file meanwhile
      this.applySuggestions(res);
    } catch (err) {
      if (this.pendingFile !== file) return;
      this.extractWarnings = [`Could not read the document (${(err as Error).message}); fill in the fields by hand.`];
    } finally {
      if (this.pendingFile === file) this.extracting = false;
    }
  }

  /** Fills the fields the user has not edited from what the server read. */
  private applySuggestions(res: ExtractResult) {
    const next: Partial<DocumentMetadata> = { ...this.draft };
    const put = (field: keyof DocumentMetadata, value: string | null) => {
      if (value && !this.touched.has(field)) (next as Record<string, unknown>)[field] = value;
    };
    put('type', res.type);
    put('title', res.title);
    put('document_date', res.document_date);
    put('reference', res.reference);
    put('counterparty', res.counterparty);
    put('amount', res.amount);
    put('currency', res.currency);
    this.draft = next;
    this.recognisedText = res.text;
    this.extractWarnings = res.warnings;
  }

  private onFieldEdited(e: Event) {
    const name = (e.target as HTMLElement).getAttribute?.('name');
    if (name) this.touched.add(name);
  }

  private revokePreview() {
    if (this.previewUrl) URL.revokeObjectURL(this.previewUrl);
    this.previewUrl = '';
  }

  private openEdit(item: Document) {
    this.editing = item;
    this.pickedEmployees = item.links.filter((l) => l.kind === 'employee').map((l) => String(l.id));
    this.pickedClients = item.links.filter((l) => l.kind === 'client').map((l) => String(l.id));
    this.pickedContracts = item.links.filter((l) => l.kind === 'contract').map((l) => String(l.id));
    this.pickedBankAccounts = item.links.filter((l) => l.kind === 'bank_account').map((l) => String(l.id));
    this.resetFormState();
    this.dialogOpen = true;
  }

  private history(id: number, label: string) {
    void (this.renderRoot.querySelector('audit-dialog') as AuditDialog).show('document', id, label);
  }

  private resetFormState() {
    this.formError = '';
    this.fieldErrors = {};
    this.saving = false;
  }

  private readPicked(form: HTMLFormElement, name: string): string[] {
    const el = form.querySelector(`wa-select[name="${name}"]`) as (HTMLElement & { value: string | string[] }) | null;
    if (!el) return [];
    const v = el.value;
    return (Array.isArray(v) ? v : v ? [v] : []).filter(Boolean);
  }

  private async save(e: Event) {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const v = formValues(form);
    const links: DocumentLinkInput[] = [
      ...this.readPicked(form, 'employee_ids').map((id) => ({ employee_id: Number(id) })),
      ...this.readPicked(form, 'client_ids').map((id) => ({ client_id: Number(id) })),
      ...this.readPicked(form, 'contract_ids').map((id) => ({ contract_id: Number(id) })),
      ...this.readPicked(form, 'bank_account_ids').map((id) => ({ bank_account_id: Number(id) })),
    ];
    const metadata: DocumentMetadata = {
      type: v.type ?? 'other',
      title: v.title ?? '',
      document_date: v.document_date,
      reference: v.reference,
      counterparty: v.counterparty,
      amount: v.amount,
      currency: v.amount ? (v.currency ?? 'EUR') : null,
      notes: v.notes,
      links,
    };
    this.saving = true;
    this.formError = '';
    this.fieldErrors = {};
    try {
      if (this.editing) {
        await api.put(`/api/documents/${this.editing.id}`, metadata);
      } else {
        const file = this.pendingFile;
        if (!file) {
          this.fieldErrors = { file: 'choose a file' };
          return;
        }
        const fd = new FormData();
        fd.append('metadata', JSON.stringify(metadata));
        fd.append('file', file, file.name);
        await api.upload('/api/documents', fd);
      }
      this.dialogOpen = false;
      this.pendingFile = null;
      this.revokePreview();
      await this.load();
    } catch (err) {
      if (err instanceof ApiError) {
        this.formError = err.message;
        this.fieldErrors = err.fields;
      } else {
        this.formError = (err as Error).message;
      }
    } finally {
      this.saving = false;
    }
  }

  private async deleteItem(item: Document) {
    if (!confirm(`Delete "${item.title}"? The file is kept in storage and can be restored by an administrator.`)) return;
    try {
      await api.delete(`/api/documents/${item.id}`);
      await this.load();
    } catch (err) {
      this.error = (err as Error).message;
    }
  }

  // ---- render ----

  render() {
    const active = this.activeObjectFilter();
    return html`
      <div class="page-header">
        <h2>Documents</h2>
        <span class="spacer"></span>
        <wa-button variant="brand" @click=${this.openPicker}>Upload document</wa-button>
        <input class="picker" type="file" accept=${acceptedFileTypes} hidden @change=${this.onPicked} />
      </div>
      ${this.dragging ? html`<div class="drop-overlay">Drop to upload</div>` : nothing}
      <div class="filters">
        <wa-select label="Type" .value=${this.filters.type} with-clear @change=${(e: Event) => this.setFilter('type', (e.target as HTMLSelectElement).value)}>
          ${typeOptions.map(([value, label]) => html`<wa-option value=${value}>${label}</wa-option>`)}
        </wa-select>
        <wa-input label="Search" placeholder="title, reference, counterparty" .value=${this.filters.q} with-clear @input=${this.onSearchInput}></wa-input>
        ${active
          ? html`
              <wa-tag variant="brand" with-remove @wa-remove=${this.clearObjectFilter}>
                ${linkKindLabels[active.kind]}: ${active.label}
              </wa-tag>
            `
          : nothing}
      </div>
      ${this.error ? html`<wa-callout variant="danger">${this.error}</wa-callout>` : nothing}
      ${this.renderTable()} ${this.renderDialog()}
      <audit-dialog></audit-dialog>
    `;
  }

  private renderTable() {
    if (this.loading) return html`<wa-spinner></wa-spinner>`;
    if (this.items.length === 0) return html`<div class="empty">No documents match.</div>`;
    return html`
      <div class="table-wrap"><table class="cards">
        <thead>
          <tr>
            <th class="c-date">Date</th>
            <th class="c-type">Type</th>
            <th class="c-title">Title</th>
            <th class="c-counterparty">Counterparty</th>
            <th class="c-amount">Amount</th>
            <th class="c-linked">Linked to</th>
            <th class="c-file">File</th>
            <th class="c-actions"></th>
          </tr>
        </thead>
        <tbody>
          ${this.items.map(
            (d) => html`
              <tr>
                <td class="date" data-label="Date">${d.document_date ?? html`<span class="muted">—</span>`}</td>
                <td data-label="Type"><wa-tag size="small" variant=${d.type === 'outbound_invoice' || d.type === 'inbound_invoice' ? 'brand' : 'neutral'}>${documentTypeLabels[d.type]}</wa-tag></td>
                <td data-label="Title">
                  ${d.title}
                  ${d.reference ? html`<span class="sub">${d.reference}</span>` : nothing}
                </td>
                <td data-label="Counterparty">${d.counterparty ?? html`<span class="muted">—</span>`}</td>
                <td class="num" data-label="Amount">${d.amount ? `${d.amount} ${d.currency}` : html`<span class="muted">—</span>`}</td>
                <td data-label="Linked to">
                  <div class="chips">
                    ${d.links.length === 0 ? html`<span class="muted">—</span>` : nothing}
                    ${d.links.map((l) => html`<wa-tag size="small" appearance="outlined">${linkKindLabels[l.kind]}: ${l.label}</wa-tag>`)}
                  </div>
                </td>
                <td class="file" data-label="File">
                  <a href="/api/documents/${d.id}/download" target="_blank" rel="noopener">${d.original_filename}</a>
                  <span class="sub">${formatBytes(d.size_bytes)}</span>
                </td>
                <td class="actions">
                  <wa-button size="small" appearance="plain" @click=${() => this.history(d.id, d.title)}>History</wa-button>
                  <wa-button size="small" appearance="plain" @click=${() => this.openEdit(d)}>Edit</wa-button>
                  <wa-button size="small" appearance="plain" variant="danger" @click=${() => this.deleteItem(d)}>Delete</wa-button>
                </td>
              </tr>
            `,
          )}
        </tbody>
      </table></div>
    `;
  }

  // wa-select and other children emit the same bubbling wa-after-hide when
  // their dropdown closes; only the dialog's own event should close it.
  private onDialogHidden = (e: Event) => {
    if (e.target !== e.currentTarget) return;
    this.dialogOpen = false;
    this.pendingFile = null;
    this.revokePreview();
  };

  /** The chosen file with its preview, the reading state and any warnings. */
  private renderPickedFile(fileError: string) {
    const f = this.pendingFile;
    return html`
      <div class="full picked">
        ${this.previewUrl ? html`<img src=${this.previewUrl} alt="" />` : nothing}
        <div class="meta">
          ${f ? html`<div>${f.name} <span class="muted">· ${formatBytes(f.size)}</span></div>` : html`<div class="muted">No file chosen</div>`}
          ${fileError ? html`<div class="sub" style="color: var(--wa-color-danger-on-quiet)">${fileError}</div>` : nothing}
          ${this.extracting
            ? html`<div class="reading"><wa-spinner></wa-spinner> Reading the document…</div>`
            : this.extractWarnings.length
              ? html`<div class="sub">${this.extractWarnings.join(' ')}</div>`
              : this.recognisedText
                ? html`<div class="sub">Fields below are suggestions from the document; check them before uploading.</div>`
                : nothing}
        </div>
        <wa-button size="small" appearance="outlined" @click=${this.openPicker}>Change file</wa-button>
      </div>
    `;
  }

  private renderDialog() {
    const d = this.editing;
    const v = this.draft;
    const err = (f: string) => this.fieldErrors[f] ?? '';
    return html`
      <wa-dialog
        label=${d ? 'Edit document' : 'Upload document'}
        ?open=${this.dialogOpen}
        @wa-after-hide=${this.onDialogHidden}
        style="--width: min(48rem, calc(100vw - 1.5rem));"
      >
        <form id="document-form" @submit=${this.save} @input=${this.onFieldEdited} @change=${this.onFieldEdited}>
          ${d ? html`<div class="full muted">${d.original_filename} · ${formatBytes(d.size_bytes)} · uploaded by ${d.uploaded_by}</div>` : this.renderPickedFile(err('file'))}
          <wa-select name="type" label="Type" required .value=${d?.type ?? v.type ?? 'receipt'} hint=${err('type')}>
            ${typeOptions.map(([value, label]) => html`<wa-option value=${value}>${label}</wa-option>`)}
          </wa-select>
          <wa-input name="title" label="Title" required .value=${d?.title ?? v.title ?? ''} hint=${err('title')}></wa-input>
          <wa-input name="document_date" label="Document date" type="date" .value=${d?.document_date ?? v.document_date ?? ''} hint=${err('document_date')}></wa-input>
          <wa-input name="reference" label="Reference / number" .value=${d?.reference ?? v.reference ?? ''}></wa-input>
          <wa-input name="counterparty" label="Counterparty" .value=${d?.counterparty ?? v.counterparty ?? ''}></wa-input>
          <wa-input name="amount" label="Amount" inputmode="decimal" placeholder="120.50" .value=${d?.amount ?? v.amount ?? ''} hint=${err('amount')}></wa-input>
          <wa-select name="currency" label="Currency" .value=${d?.currency ?? v.currency ?? 'EUR'} hint=${err('currency')}>
            <wa-option value="EUR">EUR</wa-option>
            <wa-option value="MKD">MKD</wa-option>
          </wa-select>
          <wa-textarea class="full" name="notes" label="Notes" rows="2" .value=${d?.notes ?? ''}></wa-textarea>

          <wa-select class="full" name="employee_ids" label="Linked employees" multiple with-clear .value=${this.pickedEmployees} hint=${err('links')}>
            ${this.employees.map((e) => html`<wa-option value=${String(e.id)}>${e.name}</wa-option>`)}
          </wa-select>
          <wa-select class="full" name="client_ids" label="Linked clients" multiple with-clear .value=${this.pickedClients}>
            ${this.clients.map((c) => html`<wa-option value=${String(c.id)}>${c.name}</wa-option>`)}
          </wa-select>
          <wa-select class="full" name="contract_ids" label="Linked contracts" multiple with-clear .value=${this.pickedContracts}>
            ${this.contracts.map((c) => html`<wa-option value=${String(c.id)}>${this.contractLabel(c)}</wa-option>`)}
          </wa-select>
          <wa-select class="full" name="bank_account_ids" label="Linked bank accounts" multiple with-clear .value=${this.pickedBankAccounts}>
            ${this.bankAccounts.map((a) => html`<wa-option value=${String(a.id)}>${this.accountLabel(a)}</wa-option>`)}
          </wa-select>
          ${!d && this.recognisedText
            ? html`<details class="text"><summary class="muted">Recognised text</summary><pre>${this.recognisedText}</pre></details>`
            : nothing}
          ${this.formError ? html`<wa-callout class="form-error" variant="danger">${this.formError}</wa-callout>` : nothing}
        </form>
        <div slot="footer" class="footer">
          <wa-button appearance="outlined" @click=${() => (this.dialogOpen = false)}>Cancel</wa-button>
          <wa-button variant="brand" type="submit" form="document-form" ?loading=${this.saving}>${d ? 'Save' : 'Upload'}</wa-button>
        </div>
      </wa-dialog>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'documents-page': DocumentsPage;
  }
}
